import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BadgeComponent, CardBodyComponent, CardComponent, ColComponent, RowComponent } from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';
import { MyCardsDashboardLinkComponent } from 'credo';

interface MnloDashboard {
  has_lifetime: boolean;
  mnlo_sponsored_lifetimes: number;
  pool_balance: number;
  pool_turnover: number;
  expected_pool_payout: number;
  pool_paid_total: number;
  church_shares: number;
  church_pool_balance: number;
  church_pool_turnover: number;
  expected_church_payout: number;
  church_paid_total: number;
  exchange_shares: number;
}

// mnlo dashboard — shows the member's mnlo figures (lifetime, 1% pool share,
// Church Shares and the expected Church payout, Exchange Shares). App-local so mnlo can evolve
// its own dashboard independently of the shared credo MainPageComponent.
@Component({
  selector: 'app-mnlo-main-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, CardComponent, CardBodyComponent, RowComponent, ColComponent, BadgeComponent, TranslatePipe, MyCardsDashboardLinkComponent],
  template: `
    <div class="mnlo-dashboard">
      <div class="mnlo-inner">
        <!-- Card partner programme: topmost, and only for members who hold a card. -->
        <app-mycards-dashboard-link class="d-block mb-4"></app-mycards-dashboard-link>

        <c-card class="mb-4">
          <c-card-body class="text-center p-4">
            <h2 class="mb-2">{{ 'mnlo_dashboard.welcome.title' | translate }}</h2>
            <p class="text-medium-emphasis mb-0">{{ 'mnlo_dashboard.welcome.subtitle' | translate }}</p>
          </c-card-body>
        </c-card>

        @if (data(); as d) {
          <c-row class="g-4">
            <c-col [md]="6">
              <c-card class="h-100">
                <c-card-body>
                  <div class="text-medium-emphasis small text-uppercase mb-2">{{ 'mnlo_dashboard.lifetime' | translate }}</div>
                  @if (d.has_lifetime) {
                    <c-badge color="success" class="fs-6">{{ 'mnlo_dashboard.lifetime_yes' | translate }}</c-badge>
                  } @else {
                    <c-badge color="secondary" class="fs-6">{{ 'mnlo_dashboard.lifetime_no' | translate }}</c-badge>
                  }
                </c-card-body>
              </c-card>
            </c-col>

            <c-col [md]="6">
              <c-card class="h-100">
                <c-card-body>
                  <div class="text-medium-emphasis small text-uppercase mb-2">{{ 'mnlo_dashboard.pool_share' | translate }}</div>
                  <div class="text-medium-emphasis small">{{ 'mnlo_dashboard.paid_total' | translate }}</div>
                  <div class="fs-3 fw-semibold">{{ d.pool_paid_total | number: '1.2-2' }}&nbsp;USD</div>
                  <div class="text-medium-emphasis small mt-2">{{ 'mnlo_dashboard.expected' | translate }}</div>
                  <div class="fs-3 fw-semibold">{{ d.expected_pool_payout | number: '1.2-2' }}&nbsp;USD</div>
                  <div class="text-medium-emphasis small mt-2">
                    {{ 'mnlo_dashboard.pool_balance' | translate }}: {{ d.pool_balance | number: '1.2-2' }}&nbsp;USD
                  </div>
                  <div class="text-medium-emphasis small">
                    {{ 'mnlo_dashboard.pool_turnover' | translate }}: {{ d.pool_turnover | number: '1.2-2' }}&nbsp;USD
                  </div>
                </c-card-body>
              </c-card>
            </c-col>

            <c-col [md]="6">
              <c-card class="h-100">
                <c-card-body>
                  <div class="text-medium-emphasis small text-uppercase mb-2">{{ 'mnlo_dashboard.church_shares' | translate }}</div>
                  <div class="fs-3 fw-semibold">{{ d.church_shares | number }}</div>
                </c-card-body>
              </c-card>
            </c-col>

            <c-col [md]="6">
              <c-card class="h-100">
                <c-card-body>
                  <div class="text-medium-emphasis small text-uppercase mb-2">{{ 'mnlo_dashboard.expected_payout' | translate }}</div>
                  <div class="text-medium-emphasis small">{{ 'mnlo_dashboard.paid_total' | translate }}</div>
                  <div class="fs-3 fw-semibold">{{ d.church_paid_total | number: '1.2-2' }}&nbsp;USD</div>
                  <div class="text-medium-emphasis small mt-2">{{ 'mnlo_dashboard.expected' | translate }}</div>
                  <div class="fs-3 fw-semibold">{{ d.expected_church_payout | number: '1.2-2' }}&nbsp;USD</div>
                  <div class="text-medium-emphasis small mt-2">
                    {{ 'mnlo_dashboard.church_pool_balance' | translate }}: {{ d.church_pool_balance | number: '1.2-2' }}&nbsp;USD
                  </div>
                  <div class="text-medium-emphasis small">
                    {{ 'mnlo_dashboard.church_pool_turnover' | translate }}: {{ d.church_pool_turnover | number: '1.2-2' }}&nbsp;USD
                  </div>
                </c-card-body>
              </c-card>
            </c-col>

            <!-- Exchange Shares: count only, same card as Church Shares. There is no
                 Exchange pool account, so no payout card sits next to it. -->
            <c-col [md]="6">
              <c-card class="h-100">
                <c-card-body>
                  <div class="text-medium-emphasis small text-uppercase mb-2">{{ 'mnlo_dashboard.exchange_shares' | translate }}</div>
                  <div class="fs-3 fw-semibold">{{ d.exchange_shares | number }}</div>
                </c-card-body>
              </c-card>
            </c-col>
          </c-row>
        }
      </div>
    </div>
  `,
  styles: `
    .mnlo-dashboard {
      display: flex;
      justify-content: center;
      padding: 1.5rem;
    }
    .mnlo-inner {
      width: 100%;
      max-width: 960px;
    }
  `,
})
export class MainPageComponent {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);

  readonly data = signal<MnloDashboard | null>(null);

  constructor() {
    this.http.get<MnloDashboard>('/user/mnlo_dashboard')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: d => this.data.set(d),
        error: () => this.data.set(null),
      });
  }
}
